import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { interpolateColor } from "@utils/chart-utils";
import { RootState } from "..";
import { QuestionResponsesSearchFilter } from "@/models/Filter";

export interface DonutChartConfig {
  // Chart basic settings
  title: string;
  subtitle: string;
  titleBold?: boolean;
  titleUnderline?: boolean;

  // Question metadata
  questionLabel?: string;
  questionName?: string;

  // Weight and sorting
  weight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT";
  dataSorting: "ascending" | "descending" | "none";

  // Donut-specific settings
  innerRadius: number; // 0-80% (0 = pie chart, >0 = donut)
  outerRadius: number; // 60-100%
  startAngle: number; // 0-360 degrees

  // Data Labels
  dataLabelsEnabled: boolean;
  dataLabelsPosition: "inside" | "outside" | "none";
  dataLabelsFormat: string; // "{point.name}: {point.percentage:.1f}%"
  dataLabelsDistance: number; // Distance from pie edge

  // Legend
  legendEnabled: boolean;

  // Interactivity
  allowPointSelect: boolean;
  sliceOffset: number; // Distance for selected slices

  // Center Content (for donut charts)
  centerText: string;
  centerTextEnabled: boolean;
  centerTextStyle: {
    fontSize: string;
    fontWeight: string;
    color: string;
  };

  // Center Value Configuration (Emphasis)
  emphasis: "none" | "highest" | string; // "none", "highest", or slice name
  emphasisDisplayFormat: "percentage_title" | "percentage_custom";
  emphasisCustomText: string;

  // Colors & Styling
  colorPalette: string;
  customColors: string[];

  // Data formatting
  percentageFormatting: number;
  subOnePercentStyle: "<1%" | "0%";
  sliceValueFormat: "percentage" | "percentage_text";
  minSliceTextThreshold: number;
  forceShowAllValues: boolean;
  showTooltip: boolean;
  tooltipFormat: string;

  // Data
  categories: string[];
  series: Array<{
    name: string;
    data: Array<{
      name: string;
      y: number;
      color?: string;
      isGroup?: boolean;
      groupId?: string;
      groupName?: string;
    }>;
  }>;

  // Slice Grouping
  groups: Array<{
    id: string;
    name: string;
    sliceNames: string[];
    showAsSlice: boolean;
    isHidden: boolean;
  }>;
}

interface Questions extends Record<string, any> {
  id: string;
  label: string;
  name: string;
  responses: number;
}

export interface DonutChartConfigState {
  currentConfig: DonutChartConfig;
  savedConfigs: DonutChartConfig[];
  previewMode: boolean;
  question: Questions | null;
  datasetId: string | null;
  datasetName: string | null;
  questionId: string | null;
  datasetVersionId: number | null;
  activeFilters: QuestionResponsesSearchFilter[];
  // Toggle to apply/ignore filters in preview
  applyFilters: boolean;
}

const defaultDonutChartConfig: DonutChartConfig = {
  title: "Market Share Analysis",
  subtitle: "",
  titleBold: false,
  titleUnderline: false,
  weight: "DISABLED",
  dataSorting: "none",
  innerRadius: 60, // Makes it a donut (60% inner radius)
  outerRadius: 80,
  startAngle: 0,
  dataLabelsEnabled: true,
  dataLabelsPosition: "inside",
  dataLabelsFormat: "{point.name}: {point.percentage:.1f}%", // This will be dynamically updated
  dataLabelsDistance: -45, // Fixed: should be -45 for "inside" position, not 15
  legendEnabled: true,
  allowPointSelect: true,
  sliceOffset: 10,
  centerText: "",
  centerTextEnabled: true,
  centerTextStyle: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#333333",
  },
  emphasis: "none",
  emphasisDisplayFormat: "percentage_title",
  emphasisCustomText: "",
  colorPalette: "Multicolor",
  customColors: [],
  percentageFormatting: 1,
  subOnePercentStyle: "<1%",
  sliceValueFormat: "percentage",
  minSliceTextThreshold: 2,
  forceShowAllValues: false,
  showTooltip: true,
  tooltipFormat: "{series.name}: <b>{point.percentage:.1f}%</b>", // This will be dynamically updated
  categories: ["Chrome", "Safari", "Edge", "Firefox", "Other"],
  series: [
    {
      name: "Browser Share",
      data: [
        { name: "Chrome", y: 63.59, color: "#ffb09c" }, // orange
        { name: "Safari", y: 19.17, color: "#57dfbe" }, // green
        { name: "Edge", y: 4.36, color: "#757679" }, // black
        { name: "Firefox", y: 3.91, color: "#a1a7df" }, // purple
        { name: "Other", y: 8.97, color: "#c36179" }, // crimson
      ],
    },
  ],
  groups: [],
};

export const initialState: DonutChartConfigState = {
  currentConfig: { ...defaultDonutChartConfig },
  savedConfigs: [],
  previewMode: true,
  question: null,
  datasetId: null,
  datasetName: null,
  questionId: null,
  datasetVersionId: null,
  activeFilters: [],
  applyFilters: true,
};

export const donutChartConfigSlice = createSlice({
  name: "donutChartConfig",
  initialState,
  reducers: {
    togglePreviewMode: (state) => {
      state.previewMode = !state.previewMode;
    },
    updateChartTitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.title = action.payload;
    },
    updateChartSubtitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.subtitle = action.payload;
    },
    updateTitleBold: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleBold = action.payload;
    },
    updateTitleUnderline: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleUnderline = action.payload;
    },
    updateWeight: (
      state,
      action: PayloadAction<"DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT">,
    ) => {
      state.currentConfig.weight = action.payload;
    },
    updateDataSorting: (
      state,
      action: PayloadAction<"ascending" | "descending" | "none">,
    ) => {
      state.currentConfig.dataSorting = action.payload;
    },
    updateInnerRadius: (state, action: PayloadAction<number>) => {
      state.currentConfig.innerRadius = action.payload;
    },
    updateOuterRadius: (state, action: PayloadAction<number>) => {
      state.currentConfig.outerRadius = action.payload;
    },
    updateStartAngle: (state, action: PayloadAction<number>) => {
      state.currentConfig.startAngle = action.payload;
    },
    updateDataLabelsEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.dataLabelsEnabled = action.payload;
    },
    updateDataLabelsPosition: (
      state,
      action: PayloadAction<"inside" | "outside" | "none">,
    ) => {
      state.currentConfig.dataLabelsPosition = action.payload;
      // Set distance based on position and enable/disable labels
      if (action.payload === "none") {
        state.currentConfig.dataLabelsEnabled = false;
        state.currentConfig.dataLabelsDistance = 0;
      } else if (action.payload === "inside") {
        state.currentConfig.dataLabelsEnabled = true;
        state.currentConfig.dataLabelsDistance = -45;
      } else if (action.payload === "outside") {
        state.currentConfig.dataLabelsEnabled = true;
        state.currentConfig.dataLabelsDistance = 15;
      }
    },
    updateDataLabelsFormat: (state, action: PayloadAction<string>) => {
      state.currentConfig.dataLabelsFormat = action.payload;
    },
    updateDataLabelsDistance: (state, action: PayloadAction<number>) => {
      state.currentConfig.dataLabelsDistance = action.payload;
    },
    updateLegendEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.legendEnabled = action.payload;
    },
    updateAllowPointSelect: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.allowPointSelect = action.payload;
    },
    updateSliceOffset: (state, action: PayloadAction<number>) => {
      state.currentConfig.sliceOffset = action.payload;
    },
    updateCenterText: (state, action: PayloadAction<string>) => {
      state.currentConfig.centerText = action.payload;
    },
    updateCenterTextEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.centerTextEnabled = action.payload;
    },
    updateCenterTextStyle: (
      state,
      action: PayloadAction<Partial<DonutChartConfig["centerTextStyle"]>>,
    ) => {
      state.currentConfig.centerTextStyle = {
        ...state.currentConfig.centerTextStyle,
        ...action.payload,
      };
    },
    updateColorPalette: (state, action: PayloadAction<string>) => {
      state.currentConfig.colorPalette = action.payload;

      if (action.payload === "Multicolor") {
        // Update series colors based on palette - Multicolor uses the new color palette
        const multiColors = [
          "#ffb09c",
          "#57dfbe",
          "#757679",
          "#a1a7df",
          "#c36179",
          "#57a68c",
          "#57bfe7",
          "#ebdd7c",
        ];
        state.currentConfig.series[0].data.forEach((item, index) => {
          item.color = multiColors[index % multiColors.length];
        });
      } else if (action.payload === "Gradient") {
        // For gradient, interpolate between #019EDB and #00CF9C
        const startColor = "#019EDB";
        const endColor = "#00CF9C";
        const dataLength = state.currentConfig.series[0].data.length;

        state.currentConfig.series[0].data.forEach((item, index) => {
          // Calculate interpolation factor (0 to 1)
          const factor = dataLength > 1 ? index / (dataLength - 1) : 0;
          const interpolatedColor = interpolateColor(
            startColor,
            endColor,
            factor,
          );
          item.color = interpolatedColor;
        });
      } else if (action.payload === "Grayscale") {
        // For grayscale gradient, interpolate between light gray and dark gray
        const endColor = "#f0f0f0"; // Light gray
        const startColor = "#404040"; // Dark gray
        const dataLength = state.currentConfig.series[0].data.length;

        state.currentConfig.series[0].data.forEach((item, index) => {
          // Calculate interpolation factor (0 to 1)
          const factor = dataLength > 1 ? index / (dataLength - 1) : 0;
          const interpolatedColor = interpolateColor(
            startColor,
            endColor,
            factor,
          );
          item.color = interpolatedColor;
        });
      } else {
        // For other palettes, use multicolor as fallback
        const multiColors = [
          "#ffb09c",
          "#57dfbe",
          "#757679",
          "#a1a7df",
          "#c36179",
          "#57a68c",
          "#57bfe7",
          "#ebdd7c",
        ];
        state.currentConfig.series[0].data.forEach((item, index) => {
          item.color = multiColors[index % multiColors.length];
        });
      }
    },
    updateCustomColors: (state, action: PayloadAction<string[]>) => {
      state.currentConfig.customColors = action.payload;
      // Apply custom colors to data
      if (action.payload.length > 0) {
        state.currentConfig.series[0].data.forEach((item, index) => {
          item.color = action.payload[index % action.payload.length];
        });
      }
    },
    updatePercentageFormatting: (state, action: PayloadAction<number>) => {
      state.currentConfig.percentageFormatting = action.payload;
    },
    updateSubOnePercentStyle: (state, action: PayloadAction<"<1%" | "0%">) => {
      state.currentConfig.subOnePercentStyle = action.payload;
    },
    updateSliceValueFormat: (
      state,
      action: PayloadAction<"percentage" | "percentage_text">,
    ) => {
      state.currentConfig.sliceValueFormat = action.payload;
    },
    updateMinSliceTextThreshold: (state, action: PayloadAction<number>) => {
      state.currentConfig.minSliceTextThreshold = action.payload;
    },
    updateForceShowAllValues: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.forceShowAllValues = action.payload;
    },
    updateShowTooltip: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showTooltip = action.payload;
    },
    updateTooltipFormat: (state, action: PayloadAction<string>) => {
      state.currentConfig.tooltipFormat = action.payload;
    },
    updateQuestions: (state, action: PayloadAction<Questions | null>) => {
      state.question = action.payload;
      // Set default chart title to questions?.name when questions are updated
      if (action.payload?.name) {
        state.currentConfig.title = action.payload.name;
      }
      // Store question metadata in config for persistence
      if (action.payload) {
        state.currentConfig.questionLabel = action.payload.label;
        state.currentConfig.questionName = action.payload.name;
      }
    },
    updateChartMetadata: (
      state,
      action: PayloadAction<{
        datasetId: string;
        datasetName?: string;
        questionId: string;
        datasetVersionId?: number | null;
        activeFilters?: QuestionResponsesSearchFilter[];
      }>,
    ) => {
      state.datasetId = action.payload.datasetId;
      state.datasetName = action.payload.datasetName ?? null;
      state.questionId = action.payload.questionId;
      state.datasetVersionId = action.payload.datasetVersionId ?? null;
      state.activeFilters = action.payload.activeFilters ?? [];
    },
    updateChartData: (
      state,
      action: PayloadAction<{
        categories: string[];
        series: DonutChartConfig["series"];
      }>,
    ) => {
      state.currentConfig.categories = action.payload.categories;
      state.currentConfig.series = action.payload.series;
    },
    saveCurrentConfig: (state) => {
      const configToSave = { ...state.currentConfig };
      state.savedConfigs.push(configToSave);
    },
    loadConfig: (state, action: PayloadAction<DonutChartConfig>) => {
      Object.assign(state.currentConfig, action.payload);
    },
    updateEmphasis: (state, action: PayloadAction<string>) => {
      state.currentConfig.emphasis = action.payload;
    },
    updateEmphasisDisplayFormat: (
      state,
      action: PayloadAction<"percentage_title" | "percentage_custom">,
    ) => {
      state.currentConfig.emphasisDisplayFormat = action.payload;
    },
    updateEmphasisCustomText: (state, action: PayloadAction<string>) => {
      state.currentConfig.emphasisCustomText = action.payload;
    },
    addGroup: (state) => {
      const newGroup = {
        id: `group-${Date.now()}`,
        name: "",
        sliceNames: [],
        showAsSlice: true,
        isHidden: false,
      };
      state.currentConfig.groups.push(newGroup);
    },
    updateGroup: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<{
          name: string;
          sliceNames: string[];
          showAsSlice: boolean;
          isHidden: boolean;
        }>;
      }>,
    ) => {
      const group = state.currentConfig.groups.find(
        (g) => g.id === action.payload.id,
      );
      if (group) {
        Object.assign(group, action.payload.updates);

        // Force series data update to trigger chart re-render
        state.currentConfig.series = state.currentConfig.series.map(
          (series) => ({
            ...series,
            data: series.data.map((dataPoint) => ({ ...dataPoint })),
          }),
        );
      }
    },
    removeGroup: (state, action: PayloadAction<string>) => {
      state.currentConfig.groups = state.currentConfig.groups.filter(
        (g) => g.id !== action.payload,
      );
      // If removed group was emphasized, reset emphasis to none
      if (state.currentConfig.emphasis === `group:${action.payload}`) {
        state.currentConfig.emphasis = "none";
      }
    },
    toggleApplyFilters: (state) => {
      state.applyFilters = !state.applyFilters;
    },
    setApplyFilters: (state, action: PayloadAction<boolean>) => {
      state.applyFilters = action.payload;
    },
    resetToDefault: (state) => {
      Object.assign(state.currentConfig, defaultDonutChartConfig);
    },
  },
});

export const {
  togglePreviewMode,
  updateChartTitle,
  updateChartSubtitle,
  updateTitleBold,
  updateTitleUnderline,
  updateWeight,
  updateDataSorting,
  updateInnerRadius,
  updateOuterRadius,
  updateStartAngle,
  updateDataLabelsEnabled,
  updateDataLabelsPosition,
  updateDataLabelsFormat,
  updateDataLabelsDistance,
  updateLegendEnabled,
  updateAllowPointSelect,
  updateSliceOffset,
  updateCenterText,
  updateCenterTextEnabled,
  updateCenterTextStyle,
  updateColorPalette,
  updateCustomColors,
  updatePercentageFormatting,
  updateSubOnePercentStyle,
  updateSliceValueFormat,
  updateMinSliceTextThreshold,
  updateForceShowAllValues,
  updateShowTooltip,
  updateTooltipFormat,
  updateQuestions,
  updateChartMetadata,
  updateChartData,
  toggleApplyFilters,
  setApplyFilters,
  saveCurrentConfig,
  loadConfig,
  updateEmphasis,
  updateEmphasisDisplayFormat,
  updateEmphasisCustomText,
  addGroup,
  updateGroup,
  removeGroup,
  resetToDefault,
} = donutChartConfigSlice.actions;

export const donutChartConfigDispatcher = createDispatchers(
  donutChartConfigSlice.actions,
);
export const donutChartConfigSelector = (state: RootState) =>
  state.donutChartConfig;

export const donutChartConfigReducer = donutChartConfigSlice.reducer;
